import { useEffect } from 'react';
import Barcode from 'react-barcode';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';

interface OrderPdfPreviewProps {
  order: OrderSummary;
  /** Said on the folded "more" row, for what the lines it hides carry — "with sample set" on a 2.0 sticker. */
  moreNote?: string | undefined;
}

export function OrderPdfPreview({ order, moreNote }: OrderPdfPreviewProps) {
  const totalQty = order.lineItems.reduce((sum, item) => sum + item.quantity, 0);
  const trackingNo = order.deleoData?.deleoTrackingNo || '';
  const deleoOrderId = order.deleoData?.deleoOrderId || '';
  const countryCode = order.consignee?.countryCode || '';
  const barcodeValue = trackingNo;
  const currentDate = new Date().toISOString().split('T')[0];

  const PAGE_WIDTH_CM = 15; // 100x150mm landscape: 150mm = 15cm
  const PAGE_HEIGHT_CM = 10; // 100x150mm landscape: 100mm = 10cm
  const PAGE_WIDTH_PX = PAGE_WIDTH_CM * 37.8;
  const PAGE_HEIGHT_PX = PAGE_HEIGHT_CM * 37.8;

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    // A bulk run mounts one of these per label; the head wants the font once, not once per sticker.
    if (!document.querySelector(`link[href="${link.href}"]`)) {
      document.head.appendChild(link);
    }
  }, []);

  return (
    <>
      <style>
        {`
          @media print {
            @page {
              size: 15cm 10cm !important;
              margin: 0 !important;
            }
            html, body {
              width: 15cm !important;
              height: 10cm !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-area {
              width: 15cm !important;
              height: 10cm !important;
              max-width: 15cm !important;
              max-height: 10cm !important;
              margin: 0 !important;
              padding-top: 0 !important;
              page-break-after: always !important;
            }
          }
        `}
      </style>
      <div
        className="print-area"
        style={{
          paddingTop: '0px',
          paddingBottom: '27px',
          paddingLeft: '15%',
          paddingRight: '15%',
          fontSize: '9px',
          fontFamily: '"Noto Sans KR", sans-serif',
          width: `${PAGE_WIDTH_PX}px`,
          height: `${PAGE_HEIGHT_PX}px`,
          margin: '0 auto',
          backgroundColor: '#fff',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ marginBottom: '18px', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
            <div style={{ fontSize: '26px', fontWeight: 'bold' }}>DELEO</div>
            <div>
              <Barcode value={barcodeValue} format="CODE128" width={1.3} height={54} displayValue={false} />
            </div>
          </div>
          <table style={{ width: '100%', marginBottom: '6px', fontSize: '9px', borderCollapse: 'collapse' }}>
            <tr>
              <td style={{ padding: '2px 3px', textAlign: 'left', width: '10%' }}>Ref.</td>
              <td style={{ padding: '2px 3px', textAlign: 'left', width: '30%' }}>{order.orderId}</td>
              <td style={{ padding: '2px 3px', textAlign: 'left', width: '20%' }}>Tracking No.</td>
              <td style={{ padding: '2px 3px', textAlign: 'left', width: '40%' }}>{trackingNo}</td>
            </tr>
            <tr>
              <td style={{ padding: '2px 3px', textAlign: 'left', width: '18%' }}>Order ID.</td>
              <td style={{ padding: '2px 3px', textAlign: 'left', width: '22%' }}>{deleoOrderId}</td>
              <td colSpan={2} style={{ padding: '2px 3px', textAlign: 'right', width: '60%' }}>
                To. {countryCode}
              </td>
            </tr>
          </table>
        </div>

        <table style={{ marginTop: '6px', width: '100%', border: '2px solid #000', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #000' }}>
              <th
                style={{
                  padding: '8px',
                  borderRight: '2px solid #000',
                  textAlign: 'center',
                  width: '10%',
                  fontWeight: 'bold',
                }}
              >
                No.
              </th>
              <th
                style={{
                  padding: '8px',
                  borderRight: '2px solid #000',
                  textAlign: 'center',
                  width: '70%',
                  fontWeight: 'bold',
                }}
              >
                Item
              </th>
              <th style={{ padding: '8px', textAlign: 'center', width: '20%', fontWeight: 'bold' }}>Qty.</th>
            </tr>
          </thead>
          <tbody>
            {order.lineItems.slice(0, 3).map((item, index) => (
              <tr key={index} style={{ borderBottom: '2px solid #000' }}>
                <td style={{ padding: '8px', borderRight: '2px solid #000', textAlign: 'center' }}>{index + 1}</td>
                <td style={{ padding: '8px', borderRight: '2px solid #000' }}>{item.nameKor || item.name}</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>{item.quantity}</td>
              </tr>
            ))}
            {order.lineItems.length > 3 && (
              <>
                <tr style={{ borderBottom: '2px solid #000' }}>
                  <td colSpan={3} style={{ padding: '8px', textAlign: 'center' }}>
                    {order.lineItems.length - 3} more{moreNote ? ` · ${moreNote}` : ''}
                  </td>
                </tr>
                <tr style={{ borderBottom: '2px solid #000' }}>
                  <td
                    colSpan={2}
                    style={{ padding: '8px', borderRight: '2px solid #000', textAlign: 'center', fontWeight: 'bold' }}
                  >
                    Total Qty.
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>{totalQty}</td>
                </tr>
              </>
            )}
            {order.lineItems.length <= 3 && (
              <tr style={{ borderBottom: '2px solid #000' }}>
                <td
                  colSpan={2}
                  style={{ padding: '8px', borderRight: '2px solid #000', textAlign: 'center', fontWeight: 'bold' }}
                >
                  Total Qty.
                </td>
                <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>{totalQty}</td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{ marginTop: '36px', display: 'flex', justifyContent: 'space-between', fontSize: '9px' }}>
          <div>{currentDate}</div>
          <div>1 / 1</div>
        </div>
      </div>
    </>
  );
}
